<template>
  <div class="sale-trends">
    <div class="header">
      <div class="title-container">
        <!-- <span class="indicator"></span> -->
        <h3 class="title-text">设备运行数据趋势</h3>
      </div>
      <div class="tabs">
        <button 
          @click="changeRange(7)" 
          :class="['tab-btn', activeRange === 7 ? 'active' : '']"
        >
          近7天
        </button>
        <button 
          @click="changeRange(30)" 
          :class="['tab-btn', activeRange === 30 ? 'active' : '']"
        >
          近30天
        </button>
      </div>
    </div>

    <div class="chart-container">
      <div ref="chartRef" class="chart-box"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import * as echarts from "echarts";
import { EquipmentLedgerHomeTrends } from "@/api/equipmentLedger.js";

const chartRef = ref(null);
let myChart = null;
const activeRange = ref(7); // 默认切换到近7天

// 存放图表数据
const trendData = ref({
  xData: [],
  duration: [],
  area: []
});

const loadTrendData = async () => {
  try {
    const res = await EquipmentLedgerHomeTrends(activeRange.value);
    if (res.code === 200 && res.data) {
      trendData.value = {
        xData: res.data.xData || [],
        duration: res.data.duration || [],
        area: res.data.area || []
      };
      updateChart();
    }
  } catch (error) {
    console.error("加载首页设备作业趋势数据失败:", error);
  }
};

const initChart = () => {
  if (!chartRef.value) return;
  myChart = echarts.init(chartRef.value);
  loadTrendData();
};

const updateChart = () => {
  if (!myChart) return;
  const { xData, duration, area } = trendData.value;

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151', fontSize: 12 },
      shadowColor: 'rgba(0, 0, 0, 0.08)',
      shadowBlur: 10,
      padding: [10, 15],
      formatter: (params) => {
        let name = params[0].name;
        let html = `<div style="font-weight: bold; margin-bottom: 5px; color: #1f2937;">${name}</div>`;
        params.forEach(item => {
          const colorSpan = `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:9px;height:9px;background-color:${item.color};"></span>`;
          const valFormatted = item.seriesName === '施工面积' ? Number(item.value).toLocaleString() : item.value;
          html += `<div style="display:flex; justify-content:space-between; align-items:center; gap:20px; margin-top:3px;">
            <span style="color: #6b7280;">${colorSpan}${item.seriesName}</span>
            <span style="font-weight: bold; color: #111827;">${valFormatted}${item.seriesName === '施工面积' ? ' ㎡' : ' h'}</span>
          </div>`;
        });
        return html;
      }
    },
    legend: {
      bottom: '0%',
      icon: 'rect',
      itemWidth: 12,
      itemHeight: 3,
      itemGap: 20,
      textStyle: { color: '#6b7280', fontSize: 12 },
      data: ['作业时长', '施工面积']
    },
    grid: {
      left: '2%',
      right: '2%',
      top: '12%',
      bottom: '10%',
      containLabel: true
    },
    xAxis: [
      {
        type: 'category',
        boundaryGap: false,
        data: xData,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#6b7280', fontSize: 10 }
      }
    ],
    yAxis: [
      {
        type: 'value',
        name: '时长 (h)',
        nameTextStyle: { color: '#9ca3af', fontSize: 11, align: 'right' },
        axisLabel: { color: '#9ca3af', fontSize: 10 },
        splitLine: { lineStyle: { color: '#f3f4f6' } }
      },
      {
        type: 'value',
        name: '面积 (㎡)',
        nameTextStyle: { color: '#9ca3af', fontSize: 11, align: 'left' },
        axisLabel: {
          color: '#9ca3af',
          fontSize: 10,
          formatter: (value) => Number(value).toLocaleString()
        },
        splitLine: { show: false }
      }
    ],
    series: [
      {
        name: '作业时长',
        type: 'line',
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 3, color: '#7e5bfd' },
        itemStyle: { color: '#7e5bfd' },
        data: duration
      },
      {
        name: '施工面积',
        type: 'line',
        yAxisIndex: 1,
        smooth: true,
        showSymbol: false,
        lineStyle: { width: 3, color: '#22c55e' },
        itemStyle: { color: '#22c55e' },
        data: area
      }
    ]
  };

  myChart.setOption(option);
};

const changeRange = (range) => {
  activeRange.value = range;
  loadTrendData();
};

const handleResize = () => {
  if (myChart) {
    myChart.resize();
  }
};

onMounted(() => {
  initChart();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
  if (myChart) {
    myChart.dispose();
  }
});
</script>

<style lang="less" scoped>
.sale-trends {
  background-color: #fff;
  border-radius: 8px;
  padding: 20px;
  margin-top: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.indicator {
  width: 4px;
  height: 16px;
  background-color: #7e5bfd;
  border-radius: 2px;
}

.title-text {
  font-size: 16px;
  font-weight: bold;
  color: #1a1a1a;
  margin: 0;
}

.tabs {
  display: flex;
  background-color: #f3f4f6;
  padding: 3px;
  border-radius: 8px;
}

.tab-btn {
  border: none;
  background: none;
  padding: 6px 18px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.25s ease;

  &:hover {
    color: #111827;
  }

  &.active {
    background-color: #fff !important;
    color: #7e5bfd;
    font-weight: bold;
    box-shadow: 0 2px 6px rgba(126, 91, 253, 0.15);
    // border: 1px solid red;
  }
}

.chart-container {
  position: relative;
  width: 100%;
  height: 300px;
}

.chart-box {
  width: 100%;
  height: 100%;
}
</style>
